import { Card } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Clock } from 'lucide-react';

const PricingTab = ({ editData, updateField, user, bank }) => {
  const pricingHistory = bank.pricingHistory || [];

  if (user?.role !== 'Admin') {
    return (
      <div className="text-center py-12">
        <p className="text-slate-600 font-semibold">Pricing Information Restricted</p>
        <p className="text-sm text-slate-500 mt-2">Only administrators can view pricing details.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
        <p className="text-sm text-amber-800 font-semibold">Admin Only - Pricing Information</p>
        <p className="text-xs text-amber-700 mt-1">Only administrators can view and edit pricing details.</p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Pricing Type</Label>
          <Select value={editData.pricingType || 'Rent'} onValueChange={(val) => updateField('pricingType', val)}>
            <SelectTrigger data-testid="detail-pricing-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Rent">Rent</SelectItem>
              <SelectItem value="Commission">Commission</SelectItem>
              <SelectItem value="Both">Both</SelectItem>
              <SelectItem value="Purchased">Purchased</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        {(editData.pricingType === 'Rent' || editData.pricingType === 'Both') && (
          <Card className="p-4 space-y-4 col-span-1">
            <h4 className="text-sm font-semibold text-slate-700">Rent Details</h4>
            <div><Label>Effective From</Label><Input data-testid="detail-rent-effective-date" type="date" value={editData.effectiveFrom || ''} onChange={(e) => updateField('effectiveFrom', e.target.value)} /></div>
            <div><Label>Rent Amount</Label><Input data-testid="detail-rent-amount" type="number" step="0.01" value={editData.rentAmount ?? ''} onChange={(e) => updateField('rentAmount', e.target.value)} /></div>
            <div><Label>Rent Cycle</Label>
              <Select value={editData.rentCycle || 'Monthly'} onValueChange={(val) => updateField('rentCycle', val)}>
                <SelectTrigger data-testid="detail-rent-cycle"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Monthly">Monthly</SelectItem><SelectItem value="Weekly">Weekly</SelectItem><SelectItem value="Daily">Daily</SelectItem></SelectContent>
              </Select>
            </div>
          </Card>
        )}
        {(editData.pricingType === 'Commission' || editData.pricingType === 'Both') && (
          <Card className="p-4 space-y-4 col-span-1">
            <h4 className="text-sm font-semibold text-slate-700">Commission Details</h4>
            {editData.pricingType === 'Commission' && (
              <div><Label>Effective From</Label><Input data-testid="detail-commission-effective-date" type="date" value={editData.effectiveFrom || ''} onChange={(e) => updateField('effectiveFrom', e.target.value)} /></div>
            )}
            <div><Label>Commission Type</Label>
              <Select value={editData.commissionType || 'Percentage'} onValueChange={(val) => updateField('commissionType', val)}>
                <SelectTrigger data-testid="detail-commission-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Percentage">Percentage</SelectItem><SelectItem value="Fixed">Fixed</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Commission Rate/Amount</Label><Input data-testid="detail-commission-rate" type="number" step="0.01" value={editData.commissionRate ?? ''} onChange={(e) => updateField('commissionRate', e.target.value)} /></div>
          </Card>
        )}
        {editData.pricingType === 'Purchased' && (
          <Card className="p-4 space-y-4 col-span-1">
            <h4 className="text-sm font-semibold text-slate-700">Purchase Details</h4>
            <div><Label>Purchase Amount</Label><Input data-testid="detail-purchase-amount" type="number" step="0.01" value={editData.purchaseAmount ?? ''} onChange={(e) => updateField('purchaseAmount', e.target.value)} /></div>
            <div><Label>Date of Purchase</Label><Input data-testid="detail-purchase-date" type="date" value={editData.purchaseDate || ''} onChange={(e) => updateField('purchaseDate', e.target.value)} /></div>
          </Card>
        )}
      </div>

      {pricingHistory.length > 0 && (
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-slate-500" />
            <h4 className="text-sm font-semibold text-slate-700">Pricing Change History</h4>
            <Badge variant="outline" className="text-xs">{pricingHistory.length} change(s)</Badge>
          </div>
          <div className="space-y-3 max-h-64 overflow-y-auto" data-testid="pricing-history-list">
            {[...pricingHistory].reverse().map((entry, idx) => (
              <div key={`${entry.timestamp}-${idx}`} className="p-3 bg-slate-50 rounded-lg border text-sm" data-testid={`pricing-history-${idx}`}>
                <div className="flex justify-between text-xs text-slate-500 mb-2">
                  <span>{new Date(entry.timestamp).toLocaleString()}</span>
                  <span className="font-medium">by {entry.changedBy}</span>
                </div>
                <div className="space-y-1">
                  {entry.changes?.map((change, cIdx) => (
                    <div key={`${change.field}-${cIdx}`} className="flex items-center gap-2 text-xs">
                      <span className="font-medium text-slate-700 w-32">{change.field}:</span>
                      <span className="text-red-500 line-through">{change.oldValue || '(empty)'}</span>
                      <span className="text-slate-400">-&gt;</span>
                      <span className="text-green-600 font-medium">{change.newValue}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default PricingTab;
